import { useQuery } from '@apollo/client/react';
import { Col, Flex, Grid, Skeleton, Typography } from 'antd';
import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import CarouselWrapper from '@guuru/react-carousel';
import QuoteSymbol from '../QuoteSymbol';
import FeedbackComment from './FeedbackComment';
import FeedbackContainer from './FeedbackContainer';
import GET_COMMUNITY_FEEDBACKS from './queries/GetPersistedCommunityFeedbacks';

const { Text, Title } = Typography;

const { useBreakpoint } = Grid;

const setInert = (
  slidesToShow: number,
  index: number,
  currentSlide: number,
) => {
  const isVisible =
    index >= currentSlide && index < currentSlide + slidesToShow;

  return isVisible ? {} : { inert: true };
};

const Feedback = function () {
  const { partnerId } = useParams();
  const { search } = useLocation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { md } = useBreakpoint();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useQuery(GET_COMMUNITY_FEEDBACKS, {
    variables: { partnerId: partnerId! },
  });
  const bestFeedbackComments = data?.community?.bestFeedbackComments || [];
  const elementsCount = bestFeedbackComments.length;

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current
        .querySelector('.slick-prev')
        ?.setAttribute('aria-label', 'Previous testimonial');
      containerRef.current
        .querySelector('.slick-next')
        ?.setAttribute('aria-label', 'Next testimonial');

      const liElements =
        containerRef.current.querySelectorAll('.slick-dots li');
      liElements?.forEach((li, i) => {
        li!.setAttribute('aria-label', `Testimonial ${i + 1}`);
      });
    }
  });

  if (!loading && !elementsCount) {
    return null;
  }

  const slidesToShow = md ? Math.min(elementsCount, 3) : 1;

  return (
    <FeedbackContainer ref={containerRef} align="center">
      <Skeleton active loading={loading}>
        <Title level={2}>{t('chat:::communityFeedbackHeadline')}</Title>
        <QuoteSymbol aria-hidden />
        <CarouselWrapper
          infinite={false}
          arrows={elementsCount > 1}
          initialSlide={currentSlide}
          afterChange={setCurrentSlide}
          slidesToShow={slidesToShow}
        >
          {bestFeedbackComments.map(({ comment, expert }, i) => (
            <Col key={comment}>
              <Flex vertical gap="small">
                <FeedbackComment level={5} italic>
                  {comment}
                </FeedbackComment>
                <Text>
                  - {t('chat:::givenTo')}
                  <Link
                    {...setInert(slidesToShow, i, currentSlide)}
                    aria-label={`Testimonial for ${expert.name}: ${comment}`}
                    to={`/${partnerId}/guurus/${expert.externalId}${search}`}
                  >
                    {` ${expert.name}`}
                  </Link>{' '}
                  -
                </Text>
              </Flex>
            </Col>
          ))}
        </CarouselWrapper>
      </Skeleton>
    </FeedbackContainer>
  );
};

export default Feedback;
